/**
 * The certificate PDF behind a skill rating — attach / replace / remove on the
 * employee's own My Skill, download wherever the rating is shown (My Skill,
 * the read-only Skills tab, Team Approvals).
 *
 * PDF only, up to MAX_SKILL_CERTIFICATE_BYTES. The browser screens the file
 * first (`skillCertificateProblem`) so a wrong one is refused before any
 * upload; the server enforces the same limits and also checks the PDF
 * signature.
 */
import type { UserSkillRating } from '@clockwise/clients/users';
import { ApiError, MAX_SKILL_CERTIFICATE_LABEL, skillCertificateProblem } from '@clockwise/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usersApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { formatBytes } from '@/lib/format-bytes';
import { queryKeys } from '@/lib/query-keys';

/** Download one employee's certificate for one skill. */
export function useCertificateDownload() {
  return useMutation({
    mutationFn: (v: { userId: string; skillId: string; fallbackName: string }) =>
      usersApi.downloadSkillCertificate(v.userId, v.skillId),
    onSuccess: ({ blob, filename }, v) => triggerDownload(blob, filename ?? v.fallbackName),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not download the certificate'),
  });
}

/**
 * A small download button for a rating's certificate file. `details` is the
 * certification it evidences (name, expiry), read in the tooltip — on the
 * read-only views this button is the only place those are shown.
 */
export function CertificateDownloadButton({
  userId,
  skillId,
  filename,
  details,
}: {
  userId: string;
  skillId: string;
  filename: string;
  details?: string;
}) {
  const download = useCertificateDownload();
  const label = details ? `${details} — download ${filename}` : `Download certificate ${filename}`;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="shrink-0 text-accent-foreground"
      title={label}
      aria-label={label}
      disabled={download.isPending}
      onClick={() => download.mutate({ userId, skillId, fallbackName: filename })}
    >
      {download.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Download className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

/**
 * Upload / remove for one of the caller's own ratings.
 *
 * Called from the rating ROW, not the popover: the popover's content unmounts
 * when it closes, and an upload outlives that. Held here, reopening mid-upload
 * still shows it in progress instead of offering a second one. Failures are
 * toasts for the same reason — a message inside a closed popover is a message
 * nobody reads, and they'd assume the file attached.
 */
export function useCertificateFileActions(skillId: string) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.userSkills.mine() });

  const upload = useMutation({
    mutationFn: (f: File) => usersApi.uploadSkillCertificate(skillId, f),
    onSuccess: (_res, f) => {
      toast.success(`Attached ${f.name}`);
      refresh();
    },
    onError: (err, f) =>
      toast.error(err instanceof ApiError ? err.message : `${f.name} could not be attached.`),
  });

  const remove = useMutation({
    mutationFn: () => usersApi.removeSkillCertificate(skillId),
    onSuccess: () => {
      toast.success('Certificate file removed');
      refresh();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'The file could not be removed.'),
  });

  const pick = (f: File | undefined) => {
    if (!f) return;
    const problem = skillCertificateProblem(f);
    if (problem) {
      toast.error(problem);
      return;
    }
    upload.mutate(f);
  };

  return {
    pick,
    remove: () => remove.mutate(),
    uploading: upload.isPending,
    busy: upload.isPending || remove.isPending,
  };
}

/**
 * The file section of the employee's own certificate popover: the attached
 * PDF (download / replace / remove), or a button to attach one.
 */
export function CertificateFileEditor({
  ownerUserId,
  skillId,
  file,
  actions,
}: {
  ownerUserId: string;
  skillId: string;
  file: UserSkillRating['certificateFile'] | undefined;
  actions: ReturnType<typeof useCertificateFileActions>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const download = useCertificateDownload();
  const { pick, remove, uploading, busy } = actions;
  const inputId = `cert-file-${skillId}`;

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={inputId}>Certificate file</Label>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="application/pdf,.pdf"
        className="sr-only"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          // Let the same file be picked again after an error.
          e.target.value = '';
        }}
      />
      {file ? (
        <div className="flex items-center gap-1.5 rounded-md border px-2 py-1.5">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-medium" title={file.filename}>
              {file.filename}
            </span>
            <span className="block text-[11px] text-muted-foreground">
              {formatBytes(file.sizeBytes)}
            </span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Download ${file.filename}`}
            title="Download"
            disabled={busy || download.isPending}
            onClick={() =>
              download.mutate({ userId: ownerUserId, skillId, fallbackName: file.filename })
            }
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove ${file.filename}`}
            title="Remove"
            disabled={busy}
            onClick={remove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
        {uploading ? 'Uploading…' : file ? 'Replace PDF' : 'Attach PDF'}
      </Button>
      <p className="text-[11px] text-muted-foreground">
        PDF only, up to {MAX_SKILL_CERTIFICATE_LABEL}.
      </p>
    </div>
  );
}
